"use client";
// ===================================================
// LensTabs — generic, controlled lens/tab navigation.
// DS-candidate (Design System ADR §8.5): reusable, not a vTask-local one-off.
// Graduate to src/components/design-system/ during the DS extraction phase.
// Brand v2: teal active underline, slate surface, RTL. Mirrors the look of
// src/components/pm/TabNav.tsx but generic (config-driven, no hardcoded tabs).
// ===================================================
import type { ElementType, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface LensTab {
  key: string;
  label: string;
  icon?: ElementType;
  status?: "active" | "placeholder" | "link";
}

interface LensTabsProps {
  tabs: LensTab[];
  activeKey: string;
  onChange: (key: string) => void;
  /**
   * Optional content rendered inside the same tab row, after the tabs. For a
   * sub-menu that belongs to one of the tabs rather than standing as a second
   * navigation row of its own — the caller draws its own separator. Purely
   * additive: consumers that pass nothing render exactly as before.
   */
  trailing?: ReactNode;
}

export function LensTabs({ tabs, activeKey, onChange, trailing }: LensTabsProps) {
  return (
    <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm" dir="rtl">
      <div className="flex overflow-x-auto px-3 scrollbar-hide">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = t.key === activeKey;
          return (
            <button
              key={t.key}
              onClick={() => onChange(t.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                active
                  ? "text-white border-b-teal-500 bg-slate-800/30"
                  : "text-slate-400 border-transparent hover:text-white hover:bg-slate-800/50"
              )}
            >
              {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
              <span>{t.label}</span>
              {t.status === "placeholder" && (
                <span className="text-[10px] text-slate-500 border border-white/10 rounded px-1 leading-tight">בקרוב</span>
              )}
            </button>
          );
        })}
        {trailing}
      </div>
    </nav>
  );
}
